import { useNavigate } from 'react-router-dom';
import {
  Title,
  AnimalTypeSelect,
  AnimalGenderSelect,
  Loader,
  UserSelect,
  ClearFilters,
  MedicalSupplySelectModal,
  DiseaseSelectModal,
  SlaughterhouseFilter,
  ClimateControlFilter,
  MedicalSupplyMethodSelect,
  FarmLocationSelectModal,
  DateWeekRangeSelector,
  WeightGroupFilter,
  TablePaginationWrapper,
  TruncatedChip,
  AnimalObservationFilter,
  EMPTY_ANIMAL_OBSERVATION_FILTER,
  ColumnFilterMenu,
  ParentGlyph,
  LegacyGrid as Grid
} from '../../elements';
import type { AnimalObservationFilterValue } from '../../elements';
import { LabeledCheckbox } from '../../elements/ui/labeled-checkbox';
import {
  AnimalGender,
  AnimalHealthIndicator,
  AnimalTrendDataType,
  AnimalType,
  AnimalWeightCategory
} from '../../models/animal';
import { useConfirmation } from '../../hooks/ConfirmationService';
import VisibilityIcon from '@mui/icons-material/Visibility';
import RadioButtonUnchecked from '@mui/icons-material/RadioButtonUnchecked';
import CheckCircleOutline from '@mui/icons-material/CheckCircleOutlined';
import { HeartPulse } from 'lucide-react';
import AnimalIco from '../../static/animal01.jpg';
import DeleteIcon from '@mui/icons-material/Delete';
import { green, amber, red, brown, blueGrey } from '@mui/material/colors';
import { DEFAULT_ROWS_PAGE_OPTIONS, DEFAULT_ROWS_PER_PAGE_MIDDLE, LoaderSize } from '../../constants/constants';
import UserRole from '../../constants/user-role';
import { useTranslation } from 'react-i18next';
import {
  DiseaseViewModel,
  MedicalSupplyMethod,
  MedicalSupplyViewModel,
  MortalityReasonOption
} from '../../models/injection';
import { LocationOption } from '../../models/locations';
import { UserOption, toUserOption } from '../../models/user';
import { ApiService } from '../../hooks';
import { useFarmDateTime } from '../../hooks/useFarmDateTime';
import { notifications } from '../../utils';
import {
  DEFAULT_FILTER_DATE_BY,
  describeParentNumbersFilter,
  filterDateByLabelKey
} from '../../utils/searchQuerySummary';
import { ExportFormat } from '../../models/documents';
import dayjs from 'dayjs';
import { DateRange, computeDefaultDateRangeMonthToCurrentWeek } from '../../utils/date-range';
import { getFilters, saveFilters, clearFilters as clearStoredFilters } from '../../utils/localStorageFilters';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  Container,
  Divider,
  FormControl,
  Icon,
  IconButton,
  InputAdornment,
  InputLabel,
  Link,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import { tokens } from '../../theme/tokens';
import { V4_BUTTON_DEFAULT, V4_ICON_BUTTON_SMALL, V4_OUTLINED_DEFAULT, V4_TABLE } from '../../theme/v4Compat';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import SortIcon from '@mui/icons-material/Sort';
import SortByAlpha from '@mui/icons-material/SortByAlpha';
import FormatListNumbered from '@mui/icons-material/FormatListNumbered';
import useAuthContext from '../../hooks/useAuthContext';
import {
  HealthSystemDALEnumsAnimalGender,
  HealthSystemDALEnumsAnimalType,
  HealthSystemDALEnumsAnimalTrendDataType,
  HealthSystemDomainAnimalsCommandsAnimalsSearchQuery,
  HealthSystemDomainAnimalsCommandsSearchAnimalsTrendDataQuery,
  HealthSystemDomainAnimalsResultAnimalsFarmSearchRequest,
  HealthSystemDomainAnimalsResultSlaughterhouseSearchQuery,
  HealthSystemDomainAnimalsResultWeightGroupSearchQuery,
  HealthSystemDomainAnimalsResultClimateControlSearchQuery
} from '../../api/HealthSystemApi';
import { AnimalSearchResultRow, toAnimalSearchResultRow } from '../../models/animal-search';
import { AnimalTrendData, toAnimalTrendData } from '../../models/animal-trend-data';
import { CompareAnimalSearchModal, TrendData } from '../../components';
import useFeatures, { FeatureType } from '../../hooks/features';
import MortalityReasonSelectModal from '../../elements/MortalityReasonSelectModal';
import { Slider } from '../../elements/ui/slider';
import { AnimalWeightCategoryLabelKeys } from '../../utils/animalutils';
import React from 'react';

const HEALTH_INDICATOR_COLOR: Record<AnimalHealthIndicator, string> = {
  [AnimalHealthIndicator.Green]: green[600],
  [AnimalHealthIndicator.Yellow]: amber[600],
  [AnimalHealthIndicator.Red]: red[600]
};

/** Which parent column a filter menu belongs to. The values are the backend's `parentSide` strings. */
type ParentSide = 'Mother' | 'Father';

const PARENT_SIDE_CONFIG: Record<
  ParentSide,
  { orderBy: string; headerKey: string; blankKey: string; columnId: string; color: string }
> = {
  Mother: {
    orderBy: 'motherNumber',
    headerKey: 'f-search-sow-number',
    blankKey: 'f-column-filter-no-sow',
    columnId: 'sowNumber',
    color: brown[400]
  },
  Father: {
    orderBy: 'fatherNumber',
    headerKey: 'f-search-boar-number',
    blankKey: 'f-column-filter-no-boar',
    columnId: 'boarNumber',
    color: blueGrey[600]
  }
};

/** The unsearched value list behind one column's menu, as the server returned it. */
interface ParentNumbersResult {
  numbers: string[];
  totalCount: number;
  hasBlanks: boolean;
}

const PARENT_SIDES: ParentSide[] = ['Mother', 'Father'];

/** What the table falls back to when a column's own sort is switched off. */
const DEFAULT_ORDER_BY = 'number';

/**
 * The sort arrow and the filter funnel in the parents header. The colour is what MUI's
 * TableSortLabel gives an active arrow (the theme's secondary text), so the column matches the sort
 * indicator on every other column instead of shouting in primary blue; the size is a step down from
 * MUI's own, because this header carries a glyph and an icon per parent. Inline rather than a
 * class: MUI's `.MuiSvgIcon-root { font-size: 1.5rem }` is injected after the stylesheet and would
 * win over one.
 */
const PARENT_HEADER_ICON_STYLE = { fontSize: '1.2rem', color: 'rgba(0, 0, 0, 0.54)' };

const HEALTH_INDICATOR_LABEL_KEY: Record<AnimalHealthIndicator, string> = {
  [AnimalHealthIndicator.Green]: 'f-com-health-indicator-green',
  [AnimalHealthIndicator.Yellow]: 'f-com-health-indicator-yellow',
  [AnimalHealthIndicator.Red]: 'f-com-health-indicator-red'
};

/**
 * A filter's sub-query, or null while the filter is off: the API reads null as "no filter". The generated
 * request type declares these sub-queries non-nullable, so the null is cast to fit it here, in one place.
 */
const whenOn = <T,>(isOn: boolean, query: T): T => (isOn ? query : (null as unknown as T));

const AnimalSearch: React.FC = () => {
  const navigate = useNavigate();
  const { state } = useAuthContext();
  const { t } = useTranslation();
  const { formatDate } = useFarmDateTime();
  const confirmDialog = useConfirmation();
  const { hasFeature } = useFeatures();

  const canSeeImportFilters = state.role === UserRole.admin && (state.businessUnits?.length ?? 0) > 0;

  const apiService = new ApiService();

  const searchFilterKey = `SEARCH-FARM-${state.farmId}`;

  // The request shape plus `medicalSupply`/`disease`/`mortalityReason`: the API only wants their ids,
  // but the pickers need the whole objects back, so they are carried here the same way `_location` is
  // below (`medicalSupply` is the only one actually read back, in applySavedSearchQuery).
  type SearchRequestWithPickedItems = HealthSystemDomainAnimalsResultAnimalsFarmSearchRequest & {
    medicalSupply?: MedicalSupplyViewModel | null;
    disease?: DiseaseViewModel | null;
    mortalityReason?: MortalityReasonOption | null;
  };

  // `_`-prefixed fields are persisted alongside the request but are not part of it.
  // `locationId` is all the API needs; the picker needs the whole location back, so
  // the object is kept here rather than re-fetched by id on every restore.
  type SavedSearchQuery = SearchRequestWithPickedItems & {
    _datePreset?: string | null;
    _location?: LocationOption | null;
  };

  // Pre-load saved dates from localStorage so DateWeekRangeSelector can initialize with them
  const [initialSavedDates, setInitialSavedDates] = React.useState<{ from: Date | null; to: Date | null }>(() => {
    const saved = getFilters<SavedSearchQuery>(searchFilterKey);
    if (!saved?.dateFrom || !saved?.dateTo) return { from: null, to: null };
    const from = dayjs(saved.dateFrom).toDate();
    const to = dayjs(saved.dateTo).toDate();
    if (isNaN(from.getTime()) || isNaN(to.getTime())) return { from: null, to: null };
    return { from, to };
  });

  const [number, setNumber] = React.useState<string | null | undefined>('');
  const [findByExternalIds, setFindByExternalIds] = React.useState(false);

  const handleAnimalNumberChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setNumber(event.target.value);
  };

  const [weightPerDayFrom, setWeightPerDayFrom] = React.useState('');
  const [weightPerDayTo, setWeightPerDayTo] = React.useState('');

  const handleWeightPerDayFrom = (event: React.ChangeEvent<HTMLInputElement>) => {
    setWeightPerDayFrom(event.target.value);
  };

  const handleWeightPerDayTo = (event: React.ChangeEvent<HTMLInputElement>) => {
    setWeightPerDayTo(event.target.value);
  };

  const [genetics, setGenetics] = React.useState<string | null | undefined>('');

  // The sow and boar column filter menus. The numbers and the menu's blanks entry are one filter:
  // picking both keeps the offspring of those parents together with the animals that have none.
  const [motherNumbers, setMotherNumbers] = React.useState<string[]>([]);
  const [fatherNumbers, setFatherNumbers] = React.useState<string[]>([]);
  const [includeWithoutMother, setIncludeWithoutMother] = React.useState(false);
  const [includeWithoutFather, setIncludeWithoutFather] = React.useState(false);

  const handleAnimalGeneticsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setGenetics(event.target.value);
  };

  const [animalType, setAnimalType] = React.useState<HealthSystemDALEnumsAnimalType>(0);
  const [selectedDateFrom, setSelectedDateFrom] = React.useState<Date | null>(null);
  const [selectedDateTo, setSelectedDateTo] = React.useState<Date | null>(null);
  const [isDateRangeReady, setIsDateRangeReady] = React.useState(false);
  const defaultDateRangeRef = React.useRef<DateRange | null>(null);
  const [gender, setGender] = React.useState<HealthSystemDALEnumsAnimalGender>(0);
  const [weanedDaysRange, setWeanedDaysRange] = React.useState<number[]>([0, 50]);

  const handleWeanedDaysChange = (newValue: number[]) => {
    setWeanedDaysRange(newValue);
  };

  const [injectionAgeFrom, setInjectionAgeFrom] = React.useState<string>('');
  const [injectionAgeTo, setInjectionAgeTo] = React.useState<string>('');

  const [squareMetersPerRange, setSquareMetersPerPigRange] = React.useState<number[]>([0, 5]);

  const handleSquareMetersPerChange = (newValue: number[]) => {
    // Snap to step and strip float artifacts (step 0.05 -> 2 decimals)
    setSquareMetersPerPigRange(newValue.map((n) => Math.round(n * 100) / 100));
  };
  const [isInjectionSearch, setIsInjectionSearch] = React.useState(false);
  const [isAntibioticFreeSearch, setIsAntibioticFreeSearch] = React.useState(false);
  const [isLactatingSearch, setIsLactatingSearch] = React.useState(false);
  const [isPregnantSearch, setIsPregnantSearch] = React.useState(false);
  const [isSquareMetersPerPigSearch, setIsSquareMetersPerPigSearch] = React.useState(false);
  const [isWeightGroupSearch, setIsWeightGroupSearch] = React.useState(false);
  const [isWeightCategorySearch, setIsWeightCategorySearch] = React.useState(false);
  const [weightCategorySearch, setWeightCategorySearch] = React.useState<AnimalWeightCategory[]>([]);
  const [isAnimalObservationSearch, setIsAnimalObservationSearch] = React.useState(false);
  const [observationSearch, setObservationSearch] = React.useState<AnimalObservationFilterValue>(
    EMPTY_ANIMAL_OBSERVATION_FILTER
  );
  const [isWeanedDaysSearch, setIsWeanedDaysSearch] = React.useState(false);
  const [isBirthCycleSearch, setIsBirthCycleSearch] = React.useState(false);
  const [birthCycleRange, setBirthCycleRange] = React.useState<number[]>([0, 10]);

  const handleBirthCycleRangeChange = (newValue: number[]) => {
    setBirthCycleRange(newValue);
  };

  const [isDeathSearch, setIsDeathSearch] = React.useState(false);
  const [isMortalityReasonSearch, setIsMortalityReasonSearch] = React.useState(false);
  const [isSlaughterhouseSearch, setIsSlaughterhouseSearch] = React.useState(false);
  const [isClimateControlSearch, setIsClimateControlSearch] = React.useState(false);
  const [slaughterhouseSearchQuery, setSlaughterhouseSearchQuery] =
    React.useState<HealthSystemDomainAnimalsResultSlaughterhouseSearchQuery>({});
  const [climateControlSearchQuery, setClimateControlSearchQuery] =
    React.useState<HealthSystemDomainAnimalsResultClimateControlSearchQuery>({});

  const [weightGroupSearchQuery, setWeightGroupSearchQuery] =
    React.useState<HealthSystemDomainAnimalsResultWeightGroupSearchQuery>({});

  // pagination
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(DEFAULT_ROWS_PER_PAGE_MIDDLE);
  const [total, setTotal] = React.useState(0);
  const [totalAdvancedFilterCriteria, setTotalAdvancedFilterCriteria] = React.useState(0);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
    setShouldSaveQuery(true);
    setLoaded(false);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    setLoaded(false);
  };
  // * pagination

  const [searchResult, setSearchResult] = React.useState<AnimalSearchResultRow[]>([]);
  const [selectedMedicalSupply, setSelectedMedicalSupply] = React.useState(null as MedicalSupplyViewModel | null);
  const [selectedDisease, setSelectedDisease] = React.useState(null as DiseaseViewModel | null);
  const [selectedMortalityReason, setSelectedMortalityReason] = React.useState<MortalityReasonOption | null>(null);
  const [selectedLocation, setSelectedLocation] = React.useState<LocationOption | null>(null);
  const [farmUsers, setFarmUsers] = React.useState<UserOption[]>([]);
  const [selectedUserId, setSelectedUserId] = React.useState('');
  const [isBusy, setIsBusy] = React.useState(false);
  const [loaded, setLoaded] = React.useState(false);
  const [shouldSaveQuery, setShouldSaveQuery] = React.useState(false);
  const [initialLoadComplete, setInitialLoadComplete] = React.useState(false);
  const [isBusyLoading, setIsBusyLoading] = React.useState(false);
  const [injectionMethod, setInjectionMethod] = React.useState<MedicalSupplyMethod>(MedicalSupplyMethod.Any);
  const [weekRangeResetKey, setWeekRangeResetKey] = React.useState(0);
  const [selectedDatePreset, setSelectedDatePreset] = React.useState<string | null>(null);
  const [filterDateBy, setFilterDateBy] = React.useState<string>(DEFAULT_FILTER_DATE_BY);
  const [configurationProviderType, setConfigurationProviderType] = React.useState<string | null>(null);
  const [excludeImported, setExcludeImported] = React.useState<boolean>(false);
  const [animalTrendDataType, setAnimalTrendDataType] = React.useState<HealthSystemDALEnumsAnimalTrendDataType>(
    AnimalTrendDataType.SlaughterhouseDataWeight
  );
  const [searchTrendData, setSearchTrendData] = React.useState<AnimalTrendData | null>(null);

  const activeFilters = React.useMemo(() => {
    const filters: { label: string; onDelete: () => void }[] = [];

    if (number) {
      filters.push({ label: `${t('f-filter-chip-number')}: ${number}`, onDelete: () => setNumber('') });
    }
    if (findByExternalIds) {
      filters.push({
        label: t('f-filter-external-ids'),
        onDelete: () => setFindByExternalIds(false)
      });
    }
    if (genetics) {
      filters.push({ label: `${t('f-filter-genetics')}: ${genetics}`, onDelete: () => setGenetics('') });
    }
    if (motherNumbers.length > 0 || includeWithoutMother) {
      filters.push({
        label: `${t('f-search-sow-number')}: ${describeParentNumbersFilter(motherNumbers, includeWithoutMother, t('f-column-filter-no-sow'))}`,
        onDelete: () => {
          setMotherNumbers([]);
          setIncludeWithoutMother(false);
        }
      });
    }
    if (fatherNumbers.length > 0 || includeWithoutFather) {
      filters.push({
        label: `${t('f-search-boar-number')}: ${describeParentNumbersFilter(fatherNumbers, includeWithoutFather, t('f-column-filter-no-boar'))}`,
        onDelete: () => {
          setFatherNumbers([]);
          setIncludeWithoutFather(false);
        }
      });
    }
    if (animalType !== 0) {
      filters.push({
        label: `${t('f-filter-animal-type')}: ${t(AnimalType[animalType])}`,
        onDelete: () => setAnimalType(0)
      });
    }
    if (gender !== 0) {
      filters.push({
        label: gender === AnimalGender.Male ? t('f-filter-gender-male') : t('f-filter-gender-female'),
        onDelete: () => setGender(0)
      });
    }
    if (selectedDatePreset) {
      filters.push({
        label: selectedDatePreset,
        onDelete: () => {
          setSelectedDatePreset(null);
          resetDatesToDefault();
          setWeekRangeResetKey((prev) => prev + 1);
        }
      });
    }
    if (filterDateBy !== DEFAULT_FILTER_DATE_BY) {
      filters.push({
        label: `${t('f-filter-date-by')}: ${t(filterDateByLabelKey(filterDateBy))}`,
        onDelete: () => setFilterDateBy(DEFAULT_FILTER_DATE_BY)
      });
    }
    if (weightPerDayFrom) {
      filters.push({
        label: `${t('f-filter-weight-per-day-from')}: ${weightPerDayFrom}`,
        onDelete: () => setWeightPerDayFrom('')
      });
    }
    if (weightPerDayTo) {
      filters.push({
        label: `${t('f-filter-weight-per-day-to')}: ${weightPerDayTo}`,
        onDelete: () => setWeightPerDayTo('')
      });
    }
    if (selectedLocation) {
      filters.push({
        label: `${t('f-filter-location')}: ${selectedLocation.number}`,
        onDelete: () => setSelectedLocation(null)
      });
    }
    if (isWeanedDaysSearch) {
      filters.push({ label: t('f-filter-weaned-days'), onDelete: () => setIsWeanedDaysSearch(false) });
    }
    if (isAntibioticFreeSearch) {
      filters.push({ label: t('f-filter-antibiotic-free'), onDelete: () => setIsAntibioticFreeSearch(false) });
    }
    if (isSquareMetersPerPigSearch) {
      filters.push({
        label: t('f-filter-sq-meters-per-pig'),
        onDelete: () => setIsSquareMetersPerPigSearch(false)
      });
    }
    if (isWeightGroupSearch) {
      filters.push({ label: t('f-filter-weight-group'), onDelete: () => setIsWeightGroupSearch(false) });
    }
    if (isWeightCategorySearch) {
      filters.push({
        label: t('f-filter-weight-category'),
        onDelete: () => setIsWeightCategorySearch(false)
      });
    }
    if (isAnimalObservationSearch) {
      filters.push({
        label: t('f-filter-animal-observation'),
        onDelete: () => setIsAnimalObservationSearch(false)
      });
    }
    if (isInjectionSearch) {
      filters.push({ label: t('f-filter-injection'), onDelete: () => setIsInjectionSearch(false) });
    }
    if (isDeathSearch) {
      filters.push({ label: t('f-filter-death-disease'), onDelete: () => setIsDeathSearch(false) });
    }
    if (isMortalityReasonSearch) {
      filters.push({
        label: t('f-filter-mortality-reason'),
        onDelete: () => setIsMortalityReasonSearch(false)
      });
    }
    if (isSlaughterhouseSearch) {
      filters.push({
        label: t('f-filter-slaughterhouse'),
        onDelete: () => setIsSlaughterhouseSearch(false)
      });
    }
    if (isClimateControlSearch) {
      filters.push({
        label: t('f-filter-climate-control'),
        onDelete: () => setIsClimateControlSearch(false)
      });
    }
    if (isBirthCycleSearch) {
      filters.push({
        label: t('f-filter-birth-cycle'),
        onDelete: () => setIsBirthCycleSearch(false)
      });
    }
    if (configurationProviderType) {
      filters.push({
        label: `${t('f-filter-provider-type')}: ${t('f-filter-provider-pigexpert')}`,
        onDelete: () => setConfigurationProviderType(null)
      });
    }
    if (excludeImported) {
      filters.push({
        label: t('f-filter-exclude-imported'),
        onDelete: () => setExcludeImported(false)
      });
    }

    return filters;
  }, [
    number,
    findByExternalIds,
    genetics,
    animalType,
    gender,
    selectedDatePreset,
    filterDateBy,
    weightPerDayFrom,
    weightPerDayTo,
    motherNumbers,
    fatherNumbers,
    includeWithoutMother,
    includeWithoutFather,
    selectedLocation,
    isWeanedDaysSearch,
    isAntibioticFreeSearch,
    isSquareMetersPerPigSearch,
    isWeightGroupSearch,
    isWeightCategorySearch,
    isAnimalObservationSearch,
    isInjectionSearch,
    isDeathSearch,
    isMortalityReasonSearch,
    isSlaughterhouseSearch,
    isClimateControlSearch,
    isBirthCycleSearch,
    configurationProviderType,
    excludeImported,
    t
  ]);

  const search = () => {
    if (!selectedDateFrom || !selectedDateTo) {
      return;
    }

    setIsBusy(true);
    setTotal(0);

    const searchPage = page === 0 ? 1 : page + 1;
    const searchQuery = buildSearchQuery();

    const command: HealthSystemDomainAnimalsCommandsAnimalsSearchQuery = {
      page: searchPage,
      size: rowsPerPage,
      searchQuery: searchQuery
    };

    apiService.api.animals
      .animalsSearchAnimals(command)
      .then((result) => {
        setTotal(result.data.total);
        setSearchResult(result.data.data.map(toAnimalSearchResultRow));

        if (result.data.total === 0) {
          notifications.info(t('f-animals-search-empty'));
        }
      })
      .catch((e) => {
        notifications.error(t('f-animals-search-error', { error: e.toString() }));
      })
      .finally(() => {
        setIsBusy(false);
      });
  };

  const updateTotalFilterCriteria = () => {
    const filterCount = [
      isWeanedDaysSearch,
      isAntibioticFreeSearch,
      isInjectionSearch,
      isDeathSearch,
      isMortalityReasonSearch,
      isSlaughterhouseSearch,
      isClimateControlSearch,
      isSquareMetersPerPigSearch,
      isWeightGroupSearch,
      isWeightCategorySearch,
      isAnimalObservationSearch,
      isBirthCycleSearch
    ].filter(Boolean).length;

    setTotalAdvancedFilterCriteria(filterCount);
  };

  // The type must be passed in when the caller has just changed it: reading the state variable here
  // would use the pre-update render closure and request the previously selected type.
  const searchTrend = (trendDataType: HealthSystemDALEnumsAnimalTrendDataType = animalTrendDataType) => {
    setIsBusy(true);

    const command: HealthSystemDomainAnimalsCommandsSearchAnimalsTrendDataQuery = {
      animalTrendDataType: trendDataType,
      searchQuery: buildSearchQuery()
    };

    apiService.api.animals
      .animalsGetSearchAnimalsTrendData(command)
      .then((result) => {
        setSearchTrendData(toAnimalTrendData(result.data));
      })
      .finally(() => {
        setIsBusy(false);
      });
  };

  const buildSearchQuery = (): SearchRequestWithPickedItems => {
    const command = {
      farmId: state.farmId,
      animalType: animalType,
      number: number,
      findByExternalIds: findByExternalIds || null,
      genetics: genetics,
      motherNumbers: motherNumbers.length > 0 ? motherNumbers : null,
      fatherNumbers: fatherNumbers.length > 0 ? fatherNumbers : null,
      includeWithoutMother: includeWithoutMother,
      includeWithoutFather: includeWithoutFather,
      weightPerDay: [Number(weightPerDayFrom), Number(weightPerDayTo)],
      gender: gender,
      // The DTO declares these as strings; the pickers hold a `Date`, which JSON.stringify sends as its ISO
      // string.
      dateFrom: selectedDateFrom as unknown as string | null,
      dateTo: selectedDateTo as unknown as string | null,
      filterDateBy: filterDateBy,

      isInjectionSearch: isInjectionSearch,
      isDeathSearch: isDeathSearch,
      isMortalityReasonSearch: isMortalityReasonSearch,
      isSlaughterhouseSearch: isSlaughterhouseSearch,
      isClimateControlSearch: isClimateControlSearch,
      isAntibioticFreeSearch: isAntibioticFreeSearch,
      isWeanedDaysSearch: isWeanedDaysSearch,
      isSquareMetersPerPigSearch: isSquareMetersPerPigSearch,
      isWeightGroupSearch: isWeightGroupSearch,
      isWeightCategorySearch: isWeightCategorySearch,
      isAnimalObservationSearch: isAnimalObservationSearch,
      isBirthCycleSearch: isBirthCycleSearch,
      includeChildrenCount: true,
      // The search grid has no injection-history column, so leave the extra join off.
      includeInjectionHistory: false,

      weanedDays: weanedDaysRange,
      medicalSupplyId: isInjectionSearch ? selectedMedicalSupply?.id || null : null,
      medicalSupply: isInjectionSearch ? selectedMedicalSupply : null,
      userId: isInjectionSearch ? selectedUserId : null,
      isLactating: isInjectionSearch ? isLactatingSearch : null,
      isPregnant: isInjectionSearch ? isPregnantSearch : null,
      injectionSearchAgeRange: isInjectionSearch
        ? [injectionAgeFrom !== '' ? Number(injectionAgeFrom) : 0, injectionAgeTo !== '' ? Number(injectionAgeTo) : 999]
        : null,
      diseaseId: isDeathSearch ? selectedDisease?.id || null : null,
      disease: isDeathSearch ? selectedDisease : null,
      mortalityReasonId: isMortalityReasonSearch ? selectedMortalityReason?.id || null : null,
      mortalityReason: isMortalityReasonSearch ? selectedMortalityReason : null,
      slaughterhouseSearch: whenOn(isSlaughterhouseSearch, slaughterhouseSearchQuery),
      weightGroupSearch: whenOn(isWeightGroupSearch, weightGroupSearchQuery),
      weightCategorySearch: isWeightCategorySearch ? weightCategorySearch : null,
      // Nothing picked is a valid state: the backend then matches animals flagged with anything.
      observationCategories:
        isAnimalObservationSearch && observationSearch.categories.length > 0 ? observationSearch.categories : null,
      observationGeneticFailures:
        isAnimalObservationSearch && observationSearch.geneticFailures.length > 0
          ? observationSearch.geneticFailures
          : null,
      climateControlSearch: whenOn(isClimateControlSearch, climateControlSearchQuery),
      birthCycleSearch: whenOn(isBirthCycleSearch, { min: birthCycleRange[0], max: birthCycleRange[1] }),
      squareMetersPerPigRange: isSquareMetersPerPigSearch ? squareMetersPerRange : null,
      injectionMethod: injectionMethod,
      locationId: selectedLocation?.id || null,
      orderBy: orderBy,
      order: order,
      configurationProviderType: configurationProviderType || null,
      excludeImported: excludeImported || null
    };

    return command;
  };

  // go to animal view page (with id param)
  const viewAnimal = (id: string) => {
    navigate(`/animal/${id}`);
  };

  const onDateRangeInitialized = (defaultRange: DateRange) => {
    defaultDateRangeRef.current = defaultRange;

    if (!isDateRangeReady) {
      setSelectedDateFrom(defaultRange.from);
      setSelectedDateTo(defaultRange.to);
      setIsDateRangeReady(true);
    }

    setLoaded(false);
  };

  const resetDatesToDefault = () => {
    const defaultRange = defaultDateRangeRef.current || computeDefaultDateRangeMonthToCurrentWeek();
    setSelectedDateFrom(defaultRange.from);
    setSelectedDateTo(defaultRange.to);
  };

  const clearFilters = () => {
    setNumber('');
    setFindByExternalIds(false);
    setGender(0);
    setGenetics('');
    setMotherNumbers([]);
    setFatherNumbers([]);
    setIncludeWithoutMother(false);
    setIncludeWithoutFather(false);
    setWeightPerDayFrom('');
    setWeightPerDayTo('');
    setWeanedDaysRange([0, 50]);
    setInjectionAgeFrom('');
    setInjectionAgeTo('');
    setSquareMetersPerPigRange([0, 5]);
    setAnimalType(0);
    resetDatesToDefault();
    setIsInjectionSearch(false);
    setIsDeathSearch(false);
    setIsMortalityReasonSearch(false);
    setIsSlaughterhouseSearch(false);
    setIsClimateControlSearch(false);
    setIsAntibioticFreeSearch(false);
    setIsSquareMetersPerPigSearch(false);
    setIsWeightGroupSearch(false);
    setIsWeightCategorySearch(false);
    setWeightCategorySearch([]);
    setIsAnimalObservationSearch(false);
    setObservationSearch(EMPTY_ANIMAL_OBSERVATION_FILTER);
    setIsWeanedDaysSearch(false);
    setIsBirthCycleSearch(false);
    setBirthCycleRange([0, 10]);
    setSelectedMedicalSupply(null);
    setInjectionMethod(MedicalSupplyMethod.Any);
    setSelectedUserId('');
    setSelectedDisease(null);
    setSelectedMortalityReason(null);
    setIsLactatingSearch(false);
    setIsPregnantSearch(false);
    setSelectedLocation(null);
    setSlaughterhouseSearchQuery({});
    setClimateControlSearchQuery({});
    setWeightGroupSearchQuery({});
    setSelectedDatePreset(null);
    setFilterDateBy(DEFAULT_FILTER_DATE_BY);
    setConfigurationProviderType(null);
    setExcludeImported(false);
    setInitialSavedDates({ from: null, to: null });
    setWeekRangeResetKey((prev) => prev + 1);
    clearStoredFilters('animalSearchForm');
    clearStoredFilters(searchFilterKey);
  };

  const loadFarmUsers = () => {
    setIsBusyLoading(true);

    apiService.api.users
      .usersGetUsersAll({})
      .then((result) => {
        setFarmUsers(result.data.map(toUserOption));
        setLoaded(true);
      })
      .catch((e) => {
        notifications.error(t('f-usr-load-error', { error: e.toString() }));
      })
      .finally(() => {
        setIsBusyLoading(false);
      });
  };

  // Initial bootstrap effect - only runs once the date range is ready
  React.useEffect(() => {
    if (!isDateRangeReady || initialLoadComplete) return;

    const savedQuery = loadSavedSearchQuery();
    if (savedQuery) {
      applySavedSearchQuery(savedQuery);
    } else {
      // No saved query, trigger initial search with default dates
      setLoaded(false);
    }

    loadFarmUsers();
    setInitialLoadComplete(true);
  }, [isDateRangeReady]);

  React.useEffect(() => {
    if (shouldSaveQuery) {
      saveCurrentSearchQuery();
      setShouldSaveQuery(false);
    }
  }, [shouldSaveQuery]);

  // Update total advanced filter count whenever any filter boolean changes
  React.useEffect(() => {
    updateTotalFilterCriteria();
  }, [
    isWeanedDaysSearch,
    isAntibioticFreeSearch,
    isInjectionSearch,
    isDeathSearch,
    isMortalityReasonSearch,
    isSlaughterhouseSearch,
    isClimateControlSearch,
    isSquareMetersPerPigSearch,
    isWeightGroupSearch,
    isWeightCategorySearch,
    isAnimalObservationSearch,
    isBirthCycleSearch
  ]);

  // call search effect
  React.useEffect(() => {
    if (!loaded && selectedDateFrom && selectedDateTo) {
      search();
      searchTrend();
      prefetchParentNumbers();
      setLoaded(true);
    }
  }, [loaded, selectedDateFrom, selectedDateTo]);

  const loadSavedSearchQuery = (): SavedSearchQuery | null => {
    const saved = getFilters<SavedSearchQuery>(searchFilterKey);
    if (!saved) {
      return null;
    }
    return saved;
  };

  const applySavedSearchQuery = (query: SavedSearchQuery | null) => {
    if (query === null || !isDateRangeReady) {
      return;
    }

    setAnimalType(query.animalType);
    setNumber(query.number);
    setFindByExternalIds(query.findByExternalIds || false);
    setGender(query.gender);
    setGenetics(query.genetics);
    // Defaulted: searches saved before the sow and boar columns existed carry none of these.
    setMotherNumbers(query.motherNumbers ?? []);
    setFatherNumbers(query.fatherNumbers ?? []);
    setIncludeWithoutMother(query.includeWithoutMother ?? false);
    setIncludeWithoutFather(query.includeWithoutFather ?? false);
    // Truthy, not `!= null`: an unset input is saved as Number('') === 0, so a
    // `!= null` check would restore a literal "0" and light up the chip.
    setWeightPerDayFrom(query.weightPerDay?.[0] ? String(query.weightPerDay[0]) : '');
    setWeightPerDayTo(query.weightPerDay?.[1] ? String(query.weightPerDay[1]) : '');
    setSelectedLocation(query._location ?? null);
    setWeanedDaysRange(query.weanedDays ?? [0, 50]);

    setIsInjectionSearch(query.isInjectionSearch);
    setIsDeathSearch(query.isDeathSearch);
    setIsMortalityReasonSearch(query.isMortalityReasonSearch);
    setIsSlaughterhouseSearch(query.isSlaughterhouseSearch);
    setIsWeightGroupSearch(query.isWeightGroupSearch);
    setIsWeightCategorySearch(query.isWeightCategorySearch);
    // Defaulted: searches saved before this filter existed have no such field.
    setIsAnimalObservationSearch(query.isAnimalObservationSearch ?? false);
    setIsSquareMetersPerPigSearch(query.isSquareMetersPerPigSearch);
    setIsClimateControlSearch(query.isClimateControlSearch);

    setWeightGroupSearchQuery(query.weightGroupSearch || {});
    setWeightCategorySearch(query.weightCategorySearch || []);
    setObservationSearch({
      categories: query.observationCategories || [],
      geneticFailures: query.observationGeneticFailures || []
    });
    setSquareMetersPerPigRange(query.squareMetersPerPigRange || [0, 5]);
    setSlaughterhouseSearchQuery(query.slaughterhouseSearch || {});
    setClimateControlSearchQuery(query.climateControlSearch || {});

    if (query.isInjectionSearch) {
      setSelectedMedicalSupply(query.medicalSupply || null);
      setSelectedUserId(query.userId || '');
      setInjectionMethod(query.injectionMethod);
      setIsLactatingSearch(query.isLactating || false);
      setIsPregnantSearch(query.isPregnant || false);
      const savedAgeRange = query.injectionSearchAgeRange;
      setInjectionAgeFrom(savedAgeRange?.[0] != null ? String(savedAgeRange[0]) : '');
      setInjectionAgeTo(savedAgeRange?.[1] != null ? String(savedAgeRange[1]) : '');
    }

    setIsAntibioticFreeSearch(query.isAntibioticFreeSearch);
    setIsWeanedDaysSearch(query.isWeanedDaysSearch);
    setIsBirthCycleSearch(query.isBirthCycleSearch);
    if (query.birthCycleSearch) {
      setBirthCycleRange([query.birthCycleSearch.min ?? 0, query.birthCycleSearch.max ?? 10]);
    }

    // `order` is persisted as the open-ended `string | null` the API declares; this app only ever
    // writes 'asc'/'desc' into it, but that isn't provable from a value read back out of storage.
    setOrder(query.order as 'asc' | 'desc');
    setOrderBy(query.orderBy);
    setFilterDateBy(query.filterDateBy || DEFAULT_FILTER_DATE_BY);
    setConfigurationProviderType(query.configurationProviderType ?? null);
    setExcludeImported(query.excludeImported ?? false);

    if (query._datePreset) {
      setSelectedDatePreset(query._datePreset);
    }

    // Set dates and trigger search after all filters are applied
    // Only set dates if they are valid
    if (query.dateFrom && query.dateTo) {
      const fromDate = dayjs(query.dateFrom).toDate();
      const toDate = dayjs(query.dateTo).toDate();
      if (!isNaN(fromDate.getTime()) && !isNaN(toDate.getTime())) {
        setSelectedDateFrom(fromDate);
        setSelectedDateTo(toDate);
        // Trigger search in the next tick to ensure all state updates are applied
        setTimeout(() => setLoaded(false), 0);
      }
    }
  };

  const saveCurrentSearchQuery = () => {
    const query = buildSearchQuery();
    saveFilters<SavedSearchQuery>(searchFilterKey, {
      ...query,
      _datePreset: selectedDatePreset,
      _location: selectedLocation
    });
  };

  const querySearch = () => {
    setPage(0);
    setShouldSaveQuery(true);
    setLoaded(false);
  };

  const [order, setOrder] = React.useState<'asc' | 'desc'>('asc');
  const [orderBy, setOrderBy] = React.useState<string | null | undefined>(DEFAULT_ORDER_BY);

  const handleApplySorting = (key: string) => {
    // already selected this column -> toggle asc-desc
    if (orderBy === key) {
      setOrder(order === 'asc' ? 'desc' : 'asc');
      querySearch();
      return;
    }

    // first time selecting column -> asc
    setOrder('asc');
    setOrderBy(key);
    querySearch();
  };

  const handleWeightGroupFilterChange = (searchQuery: HealthSystemDomainAnimalsResultWeightGroupSearchQuery) => {
    setWeightGroupSearchQuery(searchQuery);
  };

  const handleSlaughterhouseFilterChange = (searchQuery: HealthSystemDomainAnimalsResultSlaughterhouseSearchQuery) => {
    setSlaughterhouseSearchQuery(searchQuery);
  };

  const handleClimateControlFilterChange = (searchQuery: HealthSystemDomainAnimalsResultClimateControlSearchQuery) => {
    setClimateControlSearchQuery(searchQuery);
  };

  const applySortDirection = (key: string, direction: 'asc' | 'desc') => {
    setOrder(direction);
    setOrderBy(key);
    querySearch();
  };

  const [parentFilterSide, setParentFilterSide] = React.useState<ParentSide>('Mother');
  const [parentFilterAnchorEl, setParentFilterAnchorEl] = React.useState<null | HTMLElement>(null);
  const [parentNumbers, setParentNumbers] = React.useState<string[]>([]);
  const [parentNumbersTotal, setParentNumbersTotal] = React.useState(0);
  const [parentNumbersHasBlanks, setParentNumbersHasBlanks] = React.useState(false);
  const [isLoadingParentNumbers, setIsLoadingParentNumbers] = React.useState(false);

  /**
   * The unsearched list per column, fetched alongside the search itself so a menu opens on its
   * values instead of on a spinner. Dropped whenever the search changes, since the values are the
   * ones the current filters leave behind.
   */
  const parentNumbersCache = React.useRef<Partial<Record<ParentSide, ParentNumbersResult>>>({});

  const showParentNumbers = (result: ParentNumbersResult) => {
    setParentNumbers(result.numbers);
    setParentNumbersTotal(result.totalCount);
    setParentNumbersHasBlanks(result.hasBlanks);
    setIsLoadingParentNumbers(false);
  };

  const openParentFilterMenu = (side: ParentSide) => (event: React.MouseEvent<HTMLElement>) => {
    setParentFilterSide(side);
    setParentFilterAnchorEl(event.currentTarget);

    const prefetched = parentNumbersCache.current[side];
    if (prefetched) {
      showParentNumbers(prefetched);
      return;
    }

    // Nothing prefetched (the search is still running, or its request failed): the menu reports its
    // empty search term once it opens, and that loads the list.
    setParentNumbers([]);
    setParentNumbersTotal(0);
    setParentNumbersHasBlanks(false);
    setIsLoadingParentNumbers(true);
  };

  const closeParentFilterMenu = () => {
    setParentFilterAnchorEl(null);
  };

  /**
   * The values behind one column's menu. They come from the server rather than from the rows on
   * screen, because the table is paged - and `take` is left off so the cap stays the backend's.
   */
  const loadParentNumbers = (side: ParentSide, search: string) => {
    const prefetched = search ? undefined : parentNumbersCache.current[side];
    if (prefetched) {
      showParentNumbers(prefetched);
      return;
    }

    setIsLoadingParentNumbers(true);

    apiService.api.animals
      .animalsGetSearchAnimalsParentNumbers({
        searchQuery: buildSearchQuery(),
        parentSide: side,
        search: search || null
      })
      .then((result) => {
        const loaded = {
          numbers: result.data.numbers,
          totalCount: result.data.totalCount,
          hasBlanks: result.data.hasAnimalsWithoutParent
        };

        if (!search) {
          parentNumbersCache.current[side] = loaded;
        }

        showParentNumbers(loaded);
      })
      .catch((e) => {
        notifications.error(t('f-animals-search-error', { error: e.toString() }));
        setIsLoadingParentNumbers(false);
      });
  };

  /**
   * Fills the cache for both columns off the back of a search. Failures stay quiet: this runs
   * without anybody having asked for it, and opening the menu retries and reports for itself.
   */
  const prefetchParentNumbers = () => {
    parentNumbersCache.current = {};

    const searchQuery = buildSearchQuery();

    PARENT_SIDES.forEach((side) => {
      apiService.api.animals
        .animalsGetSearchAnimalsParentNumbers({ searchQuery, parentSide: side, search: null })
        .then((result) => {
          parentNumbersCache.current[side] = {
            numbers: result.data.numbers,
            totalCount: result.data.totalCount,
            hasBlanks: result.data.hasAnimalsWithoutParent
          };
        })
        .catch(() => undefined);
    });
  };

  const applyParentNumbersFilter = (side: ParentSide) => (values: string[], blankSelected: boolean) => {
    if (side === 'Mother') {
      setMotherNumbers(values);
      setIncludeWithoutMother(blankSelected);
    } else {
      setFatherNumbers(values);
      setIncludeWithoutFather(blankSelected);
    }

    querySearch();
  };

  const [numberSortMenuAnchorEl, setNumberSortMenuAnchorEl] = React.useState<null | HTMLElement>(null);

  const handleNumberSortMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setNumberSortMenuAnchorEl(event.currentTarget);
  };

  const handleNumberSortMenuClose = () => {
    setNumberSortMenuAnchorEl(null);
  };

  const handleNumberSortMenuItemClick = (sortKey: string) => {
    handleNumberSortMenuClose();
    handleApplySorting(sortKey);
  };

  const renderSortableTableCell = (key: string, cellText: string) => {
    if (key === 'number') {
      const isNumberSortActive = orderBy === 'number' || orderBy === 'number-numeric';

      return (
        <TableCell key={key} sortDirection={isNumberSortActive ? order : false}>
          <div className="flex items-center">
            <Tooltip title={t('f-animal-search-sort-tooltip', 'Change sort method')}>
              <IconButton
                aria-controls="number-sort-menu"
                aria-haspopup="true"
                onClick={handleNumberSortMenuClick}
                size="small"
                sx={V4_ICON_BUTTON_SMALL}
              >
                <SortIcon />
              </IconButton>
            </Tooltip>
            <Menu
              id="number-sort-menu"
              anchorEl={numberSortMenuAnchorEl}
              keepMounted
              open={Boolean(numberSortMenuAnchorEl)}
              onClose={handleNumberSortMenuClose}
            >
              <MenuItem onClick={() => handleNumberSortMenuItemClick('number')} selected={orderBy === 'number'}>
                <ListItemIcon>
                  <SortByAlpha fontSize="small" />
                </ListItemIcon>
                <ListItemText primary={t('f-animal-search-sort-number-alpha')} />
              </MenuItem>
              <MenuItem
                onClick={() => handleNumberSortMenuItemClick('number-numeric')}
                selected={orderBy === 'number-numeric'}
              >
                <ListItemIcon>
                  <FormatListNumbered fontSize="small" />
                </ListItemIcon>
                <ListItemText primary={t('f-animal-search-sort-number-numeric')} />
              </MenuItem>
            </Menu>
            <TableSortLabel
              active={isNumberSortActive}
              direction={isNumberSortActive ? order : undefined}
              onClick={() => handleApplySorting(orderBy === 'number-numeric' ? 'number-numeric' : 'number')}
            >
              <Typography color="primary" variant="subtitle2">
                {cellText}
              </Typography>
              {isNumberSortActive ? (
                <span style={VISUALLY_HIDDEN}>{order === 'desc' ? 'sorted descending' : 'sorted ascending'}</span>
              ) : null}
            </TableSortLabel>
          </div>
        </TableCell>
      );
    }
    return (
      <TableCell key={key} sortDirection={orderBy === key ? order : false}>
        <TableSortLabel
          active={orderBy === key}
          direction={orderBy === key ? order : undefined}
          onClick={() => handleApplySorting(key)}
        >
          <Typography color="primary" variant="subtitle2">
            {cellText}
          </Typography>
          {orderBy === key ? (
            <span style={VISUALLY_HIDDEN}>{order === 'desc' ? 'sorted descending' : 'sorted ascending'}</span>
          ) : null}
        </TableSortLabel>
      </TableCell>
    );
  };

  /** The drawing that stands in for the words "sow" and "boar" in the parents column. */
  const renderParentGlyph = (side: ParentSide) => (
    <ParentGlyph side={side} color={PARENT_SIDE_CONFIG[side].color} className="h-3.5 w-7" />
  );

  /**
   * One column's entry point in the shared Parents header: the glyph that says which parent it is,
   * the sort direction while that parent is what the table is sorted by, and the filter icon -
   * filled while the column is filtered - that opens the column menu. Sorting lives in that menu,
   * so a column with two parents in it does not need two sort labels as well.
   */
  const renderParentFilterButton = (side: ParentSide) => {
    const config = PARENT_SIDE_CONFIG[side];
    const isSortActive = orderBy === config.orderBy;
    const isFiltered = isParentFilterApplied(side);
    const isActive = isSortActive || isFiltered;

    return (
      <Tooltip
        key={config.orderBy}
        title={`${t(config.headerKey)}: ${t(isFiltered ? 'f-column-filter-active' : 'f-column-filter-open')}`}
      >
        <IconButton
          id={`${config.columnId}FilterBtn`}
          size="small"
          sx={V4_ICON_BUTTON_SMALL}
          onClick={openParentFilterMenu(side)}
          className={`!rounded-md !px-1 !py-0.5 ${isActive ? '!bg-blue-50' : ''}`}
        >
          {renderParentGlyph(side)}
          {isSortActive &&
            (order === 'desc' ? (
              <ArrowDownwardIcon style={PARENT_HEADER_ICON_STYLE} />
            ) : (
              <ArrowUpwardIcon style={PARENT_HEADER_ICON_STYLE} />
            ))}
          {isFiltered ? (
            <FilterAltIcon style={PARENT_HEADER_ICON_STYLE} />
          ) : (
            <FilterAltOutlinedIcon style={PARENT_HEADER_ICON_STYLE} />
          )}
        </IconButton>
      </Tooltip>
    );
  };

  /**
   * Sow and boar share one header, and it carries no title: two narrow columns that are blank on
   * most rows read better as one, and the same drawing heads the column and marks its line in every
   * cell, so a word of header text on top of it only widened the column. Each button's tooltip
   * names its parent.
   */
  const renderParentsTableCell = () => (
    <TableCell sortDirection={orderBy === 'motherNumber' || orderBy === 'fatherNumber' ? order : false}>
      <Box className="flex items-center">{PARENT_SIDES.map(renderParentFilterButton)}</Box>
    </TableCell>
  );

  /**
   * One parent's line in the cell: its glyph and its number. Clicking the number opens that parent -
   * the search result carries the id whether or not the number resolved.
   */
  const renderParentLine = (
    side: ParentSide,
    parentId: string | null | undefined,
    parentNumber: string | null | undefined
  ) => (
    <Box key={side} className="flex items-center gap-1.5">
      <Tooltip title={t(PARENT_SIDE_CONFIG[side].headerKey)}>
        <span className="inline-flex">{renderParentGlyph(side)}</span>
      </Tooltip>
      {parentId ? (
        <Link
          component="button"
          underline="hover"
          className="!text-gray-700 hover:!text-blue-700"
          onClick={() => navigate(`/animal/${parentId}`)}
        >
          {parentNumber || '-'}
        </Link>
      ) : (
        <span className="text-gray-400">{'-'}</span>
      )}
    </Box>
  );

  const renderParentsCell = (animal: AnimalSearchResultRow) => {
    // Neither parent is known: two glyphs against two dashes would be decoration, so the cell reads
    // like every other empty cell in the table.
    if (!animal.motherId && !animal.fatherId) {
      return (
        <TableCell>
          <span className="text-gray-400">{'-'}</span>
        </TableCell>
      );
    }

    return (
      <TableCell>
        <Box className="flex flex-col gap-0.5">
          {renderParentLine('Mother', animal.motherId, animal.motherNumber)}
          {renderParentLine('Father', animal.fatherId, animal.fatherNumber)}
        </Box>
      </TableCell>
    );
  };

  const isParentFilterApplied = (side: ParentSide) =>
    side === 'Mother'
      ? motherNumbers.length > 0 || includeWithoutMother
      : fatherNumbers.length > 0 || includeWithoutFather;

  const renderCompareFunctionality = () => {
    // Saved comparisons are stored and listed per farm, so the modal is meaningless without a
    // farmId (a vet token carries none). Hide the button rather than open it on an empty farm.
    if (!state.farmId) {
      return null;
    }

    return (
      <Grid item>
        <CompareAnimalSearchModal farmId={state.farmId} searchQuery={buildSearchQuery()} />
      </Grid>
    );
  };

  const renderAnimalIsDied = (animal: AnimalSearchResultRow) => {
    const parts: string[] = [];

    if (animal.diseaseName) {
      parts.push(animal.diseaseName);
    }

    if (animal.mortalityReasonName) {
      parts.push(animal.mortalityReasonName);
    }

    if (parts.length === 0 && animal.deathReason) {
      parts.push(animal.deathReason);
    }

    const label = animal.deathDate
      ? `${t('f-search-view-died')} (${formatDate(animal.deathDate)})`
      : t('f-search-view-died');

    return `${label}${parts.length > 0 ? ': ' + parts.join(', ') : ''}`;
  };

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const isExportMenuOpen = Boolean(anchorEl);

  const handleClickExportButton = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseExport = () => {
    setAnchorEl(null);
  };

  const handleExportSearch = (exportFormat: ExportFormat) => {
    handleCloseExport();
    confirmDialog({
      dialogId: 'export-dialog',
      title: t('f-export'),
      message: t('f-export-info', { format: ExportFormat[exportFormat].toUpperCase() }),
      alertSeverity: 'info'
    }).then(() => {
      apiService.api.documents
        .documentsCreateAnimalSearchExport({ searchQuery: buildSearchQuery(), exportFormat: exportFormat })
        .then((result) => {
          if (result) {
            notifications.success(t('f-export-ok'));
          } else {
            notifications.warning(t('f-export-error', { error: '' }));
          }
        })
        .catch((e) => {
          notifications.error(t('f-export-error', { error: e.toString() }));
        });
    });
  };

  return (
    // 1280px is v4's lg, which this page was laid out against; v9's lg is 1200px.
    <Container maxWidth={false} sx={{ maxWidth: 1280 }}>
      <Grid container direction="row" sx={{ justifyContent: 'space-between' }} spacing={1}>
        <Grid item container md={4} xs={10} spacing={1}>
          <Avatar alt={t('f-animals-search')} src={AnimalIco} />
          <Grid item>
            <Title>{t('f-animals-search')}</Title>
          </Grid>
        </Grid>
      </Grid>

      <Grid container spacing={1}>
        <Grid item container direction="row" spacing={1}>
          <Grid item md={6} xs={12}>
            <form
              noValidate
              autoComplete="off"
              onSubmit={(e) => {
                e.preventDefault();
                querySearch();
              }}
            >
              <TextField
                id="animalNumberField"
                label={t('f-chip-number')}
                variant="outlined"
                size="small"
                fullWidth
                value={number}
                onChange={handleAnimalNumberChange}
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip arrow title={t('f-animals-search-external-ids')}>
                          <Checkbox
                            color="default"
                            icon={<RadioButtonUnchecked />}
                            checkedIcon={<CheckCircleOutline />}
                            checked={findByExternalIds}
                            onChange={(e) => setFindByExternalIds(e.target.checked)}
                          />
                        </Tooltip>
                      </InputAdornment>
                    )
                  }
                }}
              />
            </form>
          </Grid>

          <Grid item md={6} xs={12}>
            <form
              noValidate
              autoComplete="off"
              onSubmit={(e) => {
                e.preventDefault();
                querySearch();
              }}
            >
              <TextField
                id="animalGeneticsField"
                label={t('f-genetics')}
                variant="outlined"
                size="small"
                fullWidth
                value={genetics}
                onChange={handleAnimalGeneticsChange}
              />
            </form>
          </Grid>
        </Grid>

        <Grid item container direction="row" alignItems="center" spacing={1}>
          <Grid item xs={12} sm={3}>
            <AnimalTypeSelect
              value={animalType}
              allowAny
              cssClass="!w-full"
              onAnimalTypeChange={(type) => setAnimalType(type)}
            />
          </Grid>

          <Grid item xs={12} sm={3}>
            <AnimalGenderSelect
              value={gender}
              allowAny
              cssClass="!w-full"
              onAnimalGenderChange={(gen) => setGender(gen)}
            />
          </Grid>

          <Grid item xs={12} sm={3}>
            <FormControl variant="outlined" size="small" fullWidth>
              <InputLabel id="filter-date-by-label">{t('f-filter-date-by')}</InputLabel>
              <Select
                labelId="filter-date-by-label"
                value={filterDateBy}
                label={t('f-filter-date-by')}
                onChange={(e) => setFilterDateBy(e.target.value as string)}
              >
                <MenuItem value="DateOfBirth">{t('f-filter-date-by-date-of-birth')}</MenuItem>
                <MenuItem value="RegisteredAt">{t('f-filter-date-by-registered-at')}</MenuItem>
                <MenuItem value="DeathDate">{t('f-filter-date-by-death-date')}</MenuItem>
                <MenuItem value="SlaughteredDate">{t('f-filter-date-by-slaughtered-date')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        <DateWeekRangeSelector
          key={weekRangeResetKey}
          onInitialized={onDateRangeInitialized}
          onChange={(fromDate, toDate) => {
            setSelectedDateFrom(fromDate);
            setSelectedDateTo(toDate);
          }}
          onPresetSelected={(name) => setSelectedDatePreset(name)}
          initialFromDate={initialSavedDates.from}
          initialToDate={initialSavedDates.to}
          fullWidth
          monthsBack={6}
          allowFuture={false}
        />

        <Grid item container direction="row" alignItems="center" spacing={1}>
          <Grid item md={3} sm={6} xs={12}>
            <form
              noValidate
              autoComplete="off"
              onSubmit={(e) => {
                e.preventDefault();
                querySearch();
              }}
            >
              <TextField
                id="animalWeightPerDayFrom"
                label={t('f-animal-weight-per-day-from')}
                variant="outlined"
                size="small"
                fullWidth
                value={weightPerDayFrom}
                onChange={handleWeightPerDayFrom}
                slotProps={{
                  input: {
                    endAdornment: <InputAdornment position="end">{'<'}</InputAdornment>
                  }
                }}
              />
            </form>
          </Grid>

          <Grid item md={3} sm={6} xs={12}>
            <form
              noValidate
              autoComplete="off"
              onSubmit={(e) => {
                e.preventDefault();
                querySearch();
              }}
            >
              <TextField
                id="animalWeightPerDayTo"
                label={t('f-animal-weight-per-day-to')}
                variant="outlined"
                size="small"
                fullWidth
                value={weightPerDayTo}
                onChange={handleWeightPerDayTo}
                slotProps={{
                  input: {
                    endAdornment: <InputAdornment position="end">{'>'}</InputAdornment>
                  }
                }}
              />
            </form>
          </Grid>

          <Grid item md={3} sm={6} xs={12}>
            <FarmLocationSelectModal
              disableAny={false}
              location={selectedLocation}
              fullWidth
              // Its old class only ever took effect for max-width: the button's own margin and min-width won.
              cssClass="!max-w-[180px]"
              onChange={(location) => {
                setSelectedLocation(location);
              }}
            />
          </Grid>
        </Grid>

        {canSeeImportFilters && (
          <Grid item container direction="row" alignItems="center" spacing={1}>
            <Grid item xs={12} sm={3}>
              <FormControl variant="outlined" size="small" fullWidth>
                <InputLabel id="import-filters-label">{t('f-filter-provider-type')}</InputLabel>
                <Select
                  labelId="import-filters-label"
                  value={excludeImported ? 'excludeImported' : (configurationProviderType ?? '')}
                  label={t('f-filter-provider-type')}
                  onChange={(e) => {
                    const val = e.target.value as string;
                    setConfigurationProviderType(val === 'PigExpert' ? 'PigExpert' : null);
                    setExcludeImported(val === 'excludeImported');
                  }}
                >
                  <MenuItem value="">{t('f-filter-provider-any')}</MenuItem>
                  <MenuItem value="PigExpert">{t('f-filter-provider-pigexpert')}</MenuItem>
                  <MenuItem value="excludeImported">{t('f-filter-exclude-imported')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        )}

        {/* Advanced search */}
        <Grid item container direction="row" alignItems="center">
          <Accordion>
            <AccordionSummary sx={{ display: 'flex', justifyContent: 'space-between' }} expandIcon={<ExpandMoreIcon />}>
              <Grid container spacing={1}>
                <Grid item>
                  <Title>{t('f-animals-search-advanced')}</Title>
                </Grid>
                <Grid item>
                  <Chip label={totalAdvancedFilterCriteria} variant="outlined" />
                </Grid>
              </Grid>
            </AccordionSummary>
            <AccordionDetails>
              <Grid item container direction="row" spacing={1} sx={{ mt: '2px' }}>
                <Paper variant="outlined" sx={{ width: '100%', p: '5px' }}>
                  <Grid container spacing={1} sx={{ alignItems: 'center' }}>
                    <Grid item xs={12} sm={4}>
                      <LabeledCheckbox
                        checked={isWeanedDaysSearch}
                        onCheckedChange={(checked) => setIsWeanedDaysSearch(checked)}
                        label={t('f-search-weaned-days-range')}
                        className="w-full pl-2"
                      />
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <Slider
                        disabled={isBusyLoading || !isWeanedDaysSearch}
                        value={weanedDaysRange}
                        onValueChange={handleWeanedDaysChange}
                        min={0}
                        max={70}
                        showValueLabel
                        aria-label={t('f-search-weaned-days-range')}
                      />
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>

              <Grid item container direction="row" spacing={1} sx={{ mt: '2px' }}>
                <Paper variant="outlined" sx={{ width: '100%', p: '5px' }}>
                  <Grid container spacing={1} sx={{ alignItems: 'center' }}>
                    <Grid item xs={12} sm={4}>
                      <LabeledCheckbox
                        checked={isAntibioticFreeSearch}
                        onCheckedChange={(checked) => setIsAntibioticFreeSearch(checked)}
                        label={t('f-animal-antibiotic-free-only')}
                        className="w-full pl-2"
                      />
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>

              <Grid item container direction="row" spacing={1} sx={{ mt: '2px' }}>
                <Paper variant="outlined" sx={{ width: '100%', p: '5px' }}>
                  <Grid container spacing={1} sx={{ alignItems: 'center' }}>
                    <Grid item md={4} sm={6} xs={12}>
                      <LabeledCheckbox
                        checked={isSquareMetersPerPigSearch}
                        onCheckedChange={(checked) => setIsSquareMetersPerPigSearch(checked)}
                        label={t('f-search-square-meters-per-pig-range')}
                        className="w-full pl-2"
                      />
                    </Grid>
                    <Grid item md={4} sm={6} xs={12}>
                      {/* Invisible spacer keeps the slider track vertically centered against the checkbox */}
                      <Box aria-hidden sx={{ height: 20, mb: 1 }} />
                      <Slider
                        disabled={isBusyLoading || !isSquareMetersPerPigSearch}
                        value={squareMetersPerRange}
                        onValueChange={handleSquareMetersPerChange}
                        min={0}
                        max={5}
                        step={0.05}
                        showValueLabel
                        aria-label={t('f-search-square-meters-per-pig-range')}
                      />
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                        <Typography
                          variant="body2"
                          style={{ color: !isSquareMetersPerPigSearch ? '#bdbdbd' : 'inherit' }}
                        >
                          0 {t('f-square-meter-per-pig')}
                        </Typography>
                        <Typography
                          variant="body2"
                          style={{ color: !isSquareMetersPerPigSearch ? '#bdbdbd' : 'inherit' }}
                        >
                          5 {t('f-square-meter-per-pig')}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>

              <Grid container direction="row" spacing={1} sx={{ mt: '2px' }}>
                <Paper variant="outlined" sx={{ width: '100%', p: '5px' }}>
                  <Grid container spacing={1} sx={{ alignItems: 'center' }}>
                    <Grid item md={4} sm={6} xs={12}>
                      <LabeledCheckbox
                        checked={isWeightGroupSearch}
                        onCheckedChange={(checked) => setIsWeightGroupSearch(checked)}
                        label={t('f-search-weight-group-range')}
                        className="w-full pl-2"
                      />
                    </Grid>

                    <Grid item md={8} sm={6} xs={12} sx={{ display: 'grid' }}>
                      <WeightGroupFilter
                        disabled={isBusyLoading || !isWeightGroupSearch}
                        onChange={handleWeightGroupFilterChange}
                        value={weightGroupSearchQuery}
                      />
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>

              <Grid container direction="row" spacing={1} sx={{ mt: '2px' }}>
                <Paper variant="outlined" sx={{ width: '100%', p: '5px' }}>
                  <Grid container spacing={1} sx={{ alignItems: 'center' }}>
                    <Grid item md={4} sm={6} xs={12}>
                      <LabeledCheckbox
                        checked={isWeightCategorySearch}
                        onCheckedChange={(checked) => setIsWeightCategorySearch(checked)}
                        label={t('f-animal-weight-category-search')}
                        className="w-full pl-2"
                      />
                    </Grid>

                    <Grid item md={8} sm={6} xs={12} sx={{ display: 'grid' }}>
                      <FormControl
                        variant="standard"
                        disabled={isBusyLoading || !isWeightCategorySearch}
                        sx={{ width: '100%' }}
                      >
                        <Select
                          displayEmpty
                          value={weightCategorySearch[0] ?? ''}
                          onChange={(event) => {
                            const selected = event.target.value as AnimalWeightCategory | '';
                            setWeightCategorySearch(selected === '' ? [] : [selected]);
                          }}
                          renderValue={(selected) => {
                            const value = selected as AnimalWeightCategory | '';
                            return value === ''
                              ? t('f-select-weight-category-placeholder')
                              : t(AnimalWeightCategoryLabelKeys[value]);
                          }}
                          MenuProps={{
                            anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                            transformOrigin: { vertical: 'top', horizontal: 'left' }
                          }}
                        >
                          {[AnimalWeightCategory.Light, AnimalWeightCategory.Medium, AnimalWeightCategory.Heavy].map(
                            (category) => (
                              <MenuItem key={category} value={category}>
                                {t(AnimalWeightCategoryLabelKeys[category])}
                              </MenuItem>
                            )
                          )}
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>

              <Grid container direction="row" spacing={1} sx={{ mt: '2px' }}>
                <Paper variant="outlined" sx={{ width: '100%', p: '5px' }}>
                  <Grid container spacing={1} sx={{ alignItems: 'center' }}>
                    <Grid item md={4} sm={6} xs={12}>
                      <LabeledCheckbox
                        checked={isAnimalObservationSearch}
                        onCheckedChange={(checked) => setIsAnimalObservationSearch(checked)}
                        label={t('f-animals-search-animal-observation')}
                        className="w-full pl-2"
                      />
                    </Grid>

                    <Grid item md={8} sm={6} xs={12} sx={{ display: 'grid' }}>
                      <AnimalObservationFilter
                        disabled={isBusyLoading || !isAnimalObservationSearch}
                        value={observationSearch}
                        onChange={setObservationSearch}
                        className="w-full"
                      />
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>

              <Grid container direction="row" spacing={1} sx={{ mt: '2px' }}>
                <Paper variant="outlined" sx={{ width: '100%', p: '5px' }}>
                  <Grid container spacing={1} sx={{ alignItems: 'center' }}>
                    <Grid item md={4} sm={6} xs={12}>
                      <LabeledCheckbox
                        checked={isInjectionSearch}
                        onCheckedChange={(checked) => setIsInjectionSearch(checked)}
                        label={t('f-animals-search-injection')}
                        className="w-full pl-2"
                      />
                    </Grid>

                    <Grid item md={8} sm={6} xs={12} sx={{ display: 'grid' }}>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <UserSelect
                            allowNotSelected={true}
                            notSelectedLabel={t('f-any')}
                            users={farmUsers}
                            labelText={t('f-search-by-user')}
                            disabled={isBusyLoading || !isInjectionSearch}
                            value={farmUsers.length ? selectedUserId : ''}
                            onUserSelect={(id) => setSelectedUserId(id)}
                            cssClass="!pb-3"
                            fullWidth
                          />
                        </Grid>

                        <Grid item xs={12} sm={6} sx={{ display: 'grid' }}>
                          <MedicalSupplyMethodSelect
                            value={injectionMethod}
                            allowAny={true}
                            fullWidth
                            disabled={isBusyLoading || !isInjectionSearch}
                            onSupplyMethodChange={(value) => {
                              setInjectionMethod(value);
                            }}
                          />
                        </Grid>

                        <Grid item xs={12} sm={6} sx={{ display: 'grid' }}>
                          <MedicalSupplySelectModal
                            supply={selectedMedicalSupply}
                            fullWidth
                            disabled={isBusyLoading || !isInjectionSearch}
                            onChange={(supply) => setSelectedMedicalSupply(supply)}
                          />
                        </Grid>
                        {animalType !== AnimalType.Sow && animalType !== AnimalType.Gilt && (
                          <>
                            <Grid item xs={12} sm={3} sx={{ display: 'grid' }}>
                              <TextField
                                label={t('f-filter-age-from')}
                                variant="outlined"
                                size="small"
                                fullWidth
                                type="number"
                                disabled={isBusyLoading || !isInjectionSearch}
                                value={injectionAgeFrom}
                                onChange={(e) => setInjectionAgeFrom(e.target.value)}
                                slotProps={{ htmlInput: { min: 0, max: 999 } }}
                              />
                            </Grid>
                            <Grid item xs={12} sm={3} sx={{ display: 'grid' }}>
                              <TextField
                                label={t('f-filter-age-to')}
                                variant="outlined"
                                size="small"
                                fullWidth
                                type="number"
                                disabled={isBusyLoading || !isInjectionSearch}
                                value={injectionAgeTo}
                                onChange={(e) => setInjectionAgeTo(e.target.value)}
                                slotProps={{ htmlInput: { min: 0, max: 999 } }}
                              />
                            </Grid>
                          </>
                        )}
                        {(animalType === AnimalType.Sow || animalType === AnimalType.Gilt) && (
                          <>
                            <Grid item xs={12} sm={6} sx={{ display: 'grid' }}>
                              <LabeledCheckbox
                                checked={isLactatingSearch}
                                onCheckedChange={(checked) => {
                                  setIsLactatingSearch(checked);
                                  if (checked) setIsPregnantSearch(false);
                                }}
                                disabled={isPregnantSearch}
                                label={t('f-animal-is-lact')}
                                className="w-full pl-2"
                              />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <LabeledCheckbox
                                checked={isPregnantSearch}
                                onCheckedChange={(checked) => {
                                  setIsPregnantSearch(checked);
                                  if (checked) setIsLactatingSearch(false);
                                }}
                                disabled={isLactatingSearch}
                                label={t('f-animal-is-preg')}
                                className="w-full pl-2"
                              />
                            </Grid>
                          </>
                        )}
                      </Grid>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>

              <Grid item container direction="row" spacing={1} sx={{ mt: '2px' }}>
                <Paper variant="outlined" sx={{ width: '100%', p: '5px' }}>
                  <Grid container spacing={1} sx={{ alignItems: 'center' }}>
                    <Grid item md={4} sm={6} xs={12}>
                      <LabeledCheckbox
                        checked={isDeathSearch}
                        onCheckedChange={(checked) => setIsDeathSearch(checked)}
                        label={t('f-animals-search-death')}
                        className="w-full pl-2"
                      />
                    </Grid>

                    <Grid item md={4} sm={12} xs={12} sx={{ display: 'grid' }}>
                      <DiseaseSelectModal
                        disease={selectedDisease}
                        disabled={isBusyLoading || !isDeathSearch}
                        onChange={(disease) => setSelectedDisease(disease)}
                      />
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>

              <Grid item container direction="row" spacing={1} sx={{ mt: '2px' }}>
                <Paper variant="outlined" sx={{ width: '100%', p: '5px' }}>
                  <Grid container spacing={1} sx={{ alignItems: 'center' }}>
                    <Grid item md={4} sm={6} xs={12}>
                      <LabeledCheckbox
                        checked={isMortalityReasonSearch}
                        onCheckedChange={(checked) => setIsMortalityReasonSearch(checked)}
                        label={t('f-animals-search-mortality-reason')}
                        className="w-full pl-2"
                      />
                    </Grid>

                    <Grid item md={4} sm={12} xs={12} sx={{ display: 'grid' }}>
                      <MortalityReasonSelectModal
                        mortalityReason={selectedMortalityReason}
                        disabled={isBusyLoading || !isMortalityReasonSearch}
                        onChange={(mortalityReason) => setSelectedMortalityReason(mortalityReason)}
                      />
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>

              <Grid item container direction="row" spacing={1} sx={{ mt: '2px' }}>
                <Paper variant="outlined" sx={{ width: '100%', p: '5px' }}>
                  <Grid container spacing={1} sx={{ alignItems: 'center' }}>
                    <Grid item md={4} sm={6} xs={12}>
                      <LabeledCheckbox
                        checked={isSlaughterhouseSearch}
                        onCheckedChange={(checked) => setIsSlaughterhouseSearch(checked)}
                        label={t('f-animals-search-slaughterhouse-data')}
                        className="w-full pl-2"
                      />
                    </Grid>

                    <Grid item md={8} sm={6} xs={12} sx={{ display: 'grid' }}>
                      <SlaughterhouseFilter
                        initialValues={slaughterhouseSearchQuery}
                        disabled={isBusyLoading || !isSlaughterhouseSearch}
                        onChange={handleSlaughterhouseFilterChange}
                        value={slaughterhouseSearchQuery}
                      />
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>

              {hasFeature(FeatureType.SEARCH_BY_CLIMATE) && (
                <Grid item container direction="row" spacing={1} sx={{ mt: '2px' }}>
                  <Paper variant="outlined" sx={{ width: '100%', p: '5px' }}>
                    <Grid container spacing={1} sx={{ alignItems: 'center' }}>
                      <Grid item md={4} sm={6} xs={12}>
                        <LabeledCheckbox
                          checked={isClimateControlSearch}
                          onCheckedChange={(checked) => setIsClimateControlSearch(checked)}
                          label={t('f-animals-search-climate-controle-data')}
                          className="w-full pl-2"
                        />
                      </Grid>

                      <Grid item md={8} sm={6} xs={12} sx={{ display: 'grid' }}>
                        <ClimateControlFilter
                          disabled={isBusyLoading || !isClimateControlSearch}
                          onChange={handleClimateControlFilterChange}
                          value={climateControlSearchQuery}
                        />
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>
              )}
              <Grid item container direction="row" spacing={1} sx={{ mt: '2px' }}>
                <Paper variant="outlined" sx={{ width: '100%', p: '5px' }}>
                  <Grid container spacing={1} sx={{ alignItems: 'center' }}>
                    <Grid item xs={12} sm={4}>
                      <LabeledCheckbox
                        checked={isBirthCycleSearch}
                        onCheckedChange={(checked) => setIsBirthCycleSearch(checked)}
                        label={t('f-search-birth-cycle-range')}
                        className="w-full pl-2"
                      />
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      {/* Invisible spacer keeps the slider track vertically centered against the checkbox */}
                      <Box aria-hidden sx={{ height: 20, mb: 1 }} />
                      <Slider
                        disabled={isBusyLoading || !isBirthCycleSearch}
                        value={birthCycleRange}
                        onValueChange={handleBirthCycleRangeChange}
                        min={0}
                        max={10}
                        showValueLabel
                        aria-label={t('f-search-birth-cycle-range')}
                      />
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                        <Typography variant="body2" style={{ color: !isBirthCycleSearch ? '#bdbdbd' : 'inherit' }}>
                          0
                        </Typography>
                        <Typography variant="body2" style={{ color: !isBirthCycleSearch ? '#bdbdbd' : 'inherit' }}>
                          10
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
            </AccordionDetails>
          </Accordion>
        </Grid>

        {activeFilters.length > 0 && (
          <Grid item xs={12}>
            <div className="flex flex-wrap gap-2 mt-3 items-center">
              <span className="text-sm font-medium text-gray-600">{t('f-applied-filters')}:</span>
              {activeFilters.map((f) => (
                <Chip
                  key={f.label}
                  label={f.label}
                  size="small"
                  onDelete={f.onDelete}
                  color="primary"
                  variant="outlined"
                />
              ))}
            </div>
          </Grid>
        )}

        <Grid container justifyContent="flex-end" direction="row" item sx={{ mt: '2px' }}>
          <ClearFilters onClick={clearFilters} />

          <Button
            id="searchBtn"
            variant="contained"
            color="primary"
            size="small"
            sx={{ height: 40, m: 1 }}
            onClick={querySearch}
            disabled={isBusy}
            startIcon={<Icon>{'search'}</Icon>}
          >
            {t('f-search')}
          </Button>
        </Grid>

        {/* Results */}
        <Grid container spacing={1}>
          <Grid item xs={12} sx={{ m: 1 }}>
            <Divider />
          </Grid>

          <Grid item container sx={{ justifyContent: 'space-between' }} alignItems="center" spacing={2}>
            <Grid item>
              <Grid container direction="row" spacing={1} alignItems="center">
                <Grid item>
                  <Icon color="primary" fontSize="large">
                    {'list'}
                  </Icon>
                </Grid>
                <Grid item>
                  <Title>{t('f-search-results')}</Title>
                </Grid>
                <Grid item>
                  <Chip label={total} variant="outlined" id="totalChip" />
                </Grid>
              </Grid>
            </Grid>

            <Grid item>
              <Grid container direction="row" spacing={1} alignItems="center">
                {hasFeature(FeatureType.USE_COMPARE_FUNCTIONALITY) && renderCompareFunctionality()}

                {hasFeature(FeatureType.USE_COMPARE_FUNCTIONALITY) && (
                  <Grid item>
                    <Button
                      id="myReportsBtn"
                      variant="outlined"
                      color="inherit"
                      sx={V4_OUTLINED_DEFAULT}
                      startIcon={<Icon>{'summarize'}</Icon>}
                      onClick={() => navigate('/animal/reports')}
                    >
                      {t('f-report-my-reports')}
                    </Button>
                  </Grid>
                )}

                <Grid item>
                  <Button
                    id="exportBtn"
                    color="inherit"
                    sx={V4_BUTTON_DEFAULT}
                    variant="contained"
                    startIcon={<Icon>{'description'}</Icon>}
                    aria-controls={isExportMenuOpen ? 'basic-menu' : undefined}
                    aria-haspopup="true"
                    aria-expanded={isExportMenuOpen ? 'true' : undefined}
                    onClick={handleClickExportButton}
                  >
                    {t('f-export')}
                  </Button>
                  <Menu
                    id="basic-menu"
                    anchorEl={anchorEl}
                    open={isExportMenuOpen}
                    onClose={handleCloseExport}
                    slotProps={{
                      list: { 'aria-labelledby': 'basic-button' }
                    }}
                  >
                    <MenuItem onClick={() => handleExportSearch(ExportFormat.Excel)}>{t('f-export-excel')}</MenuItem>
                    <MenuItem onClick={() => handleExportSearch(ExportFormat.ExcelSplitWorkSheet)}>
                      {t('f-export-split-work-sheet')}
                    </MenuItem>
                    <MenuItem onClick={() => handleExportSearch(ExportFormat.Json)}>{t('f-export-json')}</MenuItem>
                  </Menu>
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <TablePaginationWrapper
              rowsPerPageOptions={DEFAULT_ROWS_PAGE_OPTIONS}
              count={total}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            >
              <Table size="small" sx={V4_TABLE} id="animalSearchResultTable">
                <TableHead sx={{ background: tokens.sidebar }}>
                  <TableRow>
                    {renderSortableTableCell('number', t('f-search-number'))}
                    {renderSortableTableCell('dateOfBirth', t('f-com-date-of-birth'))}
                    {renderSortableTableCell('genetics', t('f-genetics'))}
                    {renderParentsTableCell()}
                    <TableCell>{t('f-com-piglets-count')}</TableCell>
                    <TableCell>{t('f-com-animal-type')}</TableCell>
                    <TableCell>{t('f-com-health-indicator')}</TableCell>
                    <TableCell></TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                </TableHead>

                {!isBusy && Array.isArray(searchResult) && searchResult.length > 0 && (
                  <TableBody>
                    {searchResult.map((animal) => (
                      <TableRow key={animal.id} hover>
                        <TableCell>{animal.number}</TableCell>
                        <TableCell>{animal.dateOfBirth ? formatDate(animal.dateOfBirth) : '-'}</TableCell>
                        <TableCell>{animal.genetics || '-'}</TableCell>
                        {renderParentsCell(animal)}
                        <TableCell>{animal.childrenCount || '0'}</TableCell>
                        <TableCell>{t(AnimalType[animal.animalType])}</TableCell>
                        <TableCell>
                          <Tooltip arrow title={t(HEALTH_INDICATOR_LABEL_KEY[animal.healthIndicator])}>
                            <span className="inline-flex">
                              <HeartPulse size={20} style={{ color: HEALTH_INDICATOR_COLOR[animal.healthIndicator] }} />
                            </span>
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          {animal.isDied && (
                            <TruncatedChip
                              color="secondary"
                              // Still a v4 Chip inside: important utilities, as its makeStyles class beat v4's own styles.
                              className="!m-2 !min-w-[180px] !max-w-[180px]"
                              size="small"
                              label={renderAnimalIsDied(animal)}
                              variant="outlined"
                            />
                          )}
                          {animal.isSoftDeleted && (
                            <Chip
                              sx={{ m: 1, minWidth: 180, maxWidth: 180 }}
                              size="small"
                              avatar={<DeleteIcon />}
                              label={t('f-search-view-deleted')}
                              variant="outlined"
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          <Tooltip arrow title={t('f-search-view-animal')}>
                            <IconButton
                              color="primary"
                              size="small"
                              sx={V4_ICON_BUTTON_SMALL}
                              onClick={() => viewAnimal(animal.id)}
                            >
                              <VisibilityIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                )}
              </Table>
            </TablePaginationWrapper>

            {/* One menu for both parents: which one it belongs to follows the glyph that opened it. */}
            <ColumnFilterMenu
              anchorEl={parentFilterAnchorEl}
              onClose={closeParentFilterMenu}
              title={t(PARENT_SIDE_CONFIG[parentFilterSide].headerKey)}
              sortDirection={orderBy === PARENT_SIDE_CONFIG[parentFilterSide].orderBy ? order : null}
              onSort={(direction) => {
                applySortDirection(PARENT_SIDE_CONFIG[parentFilterSide].orderBy, direction);
                closeParentFilterMenu();
              }}
              onClearSort={() => {
                applySortDirection(DEFAULT_ORDER_BY, 'asc');
                closeParentFilterMenu();
              }}
              values={parentNumbers}
              totalCount={parentNumbersTotal}
              isLoading={isLoadingParentNumbers}
              onSearch={(term) => loadParentNumbers(parentFilterSide, term)}
              blankLabel={parentNumbersHasBlanks ? t(PARENT_SIDE_CONFIG[parentFilterSide].blankKey) : undefined}
              selectedValues={parentFilterSide === 'Mother' ? motherNumbers : fatherNumbers}
              blankSelected={parentFilterSide === 'Mother' ? includeWithoutMother : includeWithoutFather}
              onApply={applyParentNumbersFilter(parentFilterSide)}
            />
          </Grid>
        </Grid>
        {/* Results */}

        {/* Trend Analytics */}
        <Grid container spacing={1} sx={{ m: 1 }}>
          <Grid item xs={12}>
            {/* Gated on the result existing, not on it being non-empty: a type with no rows must still
                render so the selector stays reachable. TrendData renders its own no-data message. */}
            {!isBusy && searchTrendData?.trendData && (
              <TrendData
                animalTrendDataType={animalTrendDataType}
                trendData={searchTrendData.trendData}
                slaughterhouseDataAverage={searchTrendData.slaughterhouseDataAverage}
                weightAverage={searchTrendData.weightAverage}
                onAnimalTrendDataTypeChanged={(type) => {
                  setAnimalTrendDataType(type);
                  searchTrend(type);
                }}
              />
            )}
          </Grid>
        </Grid>
        {/* Trend Analytics */}

        <Grid container>
          <Loader isLoading={isBusy} size={LoaderSize.DEFAULT_SIZE} />
        </Grid>
      </Grid>
    </Container>
  );
};

export default AnimalSearch;

/** Read by screen readers, invisible on screen: the sort direction next to a sorted column's name. */
const VISUALLY_HIDDEN: React.CSSProperties = {
  border: 0,
  clip: 'rect(0 0 0 0)',
  height: 1,
  margin: -1,
  overflow: 'hidden',
  padding: 0,
  position: 'absolute',
  top: 20,
  width: 1
};
